import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { crewService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import type { CrewMember } from "../types/database";

// Define types for form state
type PaymentMethod = "Bank Transfer" | "PayPal" | "Cash" | "Other";

interface CrewFormProps {
  crewMember?: CrewMember;
  onSuccess: (crewMember: CrewMember) => void;
  orgId: string;
}

export default function CrewForm({
  crewMember,
  onSuccess,
  orgId,
}: CrewFormProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditing = !!crewMember;

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [roleType, setRoleType] = useState("");
  const [specialties, setSpecialties] = useState<string[]>([]);
  const [specialtyInput, setSpecialtyInput] = useState("");
  const [dayRate, setDayRate] = useState<string>("");
  const [hourlyRate, setHourlyRate] = useState<string>("");
  const [currency, setCurrency] = useState("GBP");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [notes, setNotes] = useState("");
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(
    null
  );

  // Address fields
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");

  // Payment details
  const [paymentMethod, setPaymentMethod] =
    useState<PaymentMethod>("Bank Transfer");
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [sortCode, setSortCode] = useState("");
  const [bankName, setBankName] = useState("");
  const [paypalEmail, setPaypalEmail] = useState("");
  const [otherPaymentDetails, setOtherPaymentDetails] = useState("");

  // Emergency contact
  const [emergencyName, setEmergencyName] = useState("");
  const [emergencyRelationship, setEmergencyRelationship] = useState("");
  const [emergencyPhone, setEmergencyPhone] = useState("");

  // Availability
  const [generalAvailability, setGeneralAvailability] = useState("");

  // Mutations
  const addCrewMutation = useMutation({
    mutationFn: (newCrewMember: any) =>
      crewService.addCrewMember(newCrewMember),
    onSuccess: (data) => onSuccess(data),
    onError: (error: any) => {
      console.error("Error adding crew member:", error);
      alert(`Error adding crew member: ${error.message || "Unknown error"}`);
    },
  });

  const updateCrewMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) =>
      crewService.updateCrewMember(id, updates, orgId),
    onSuccess: (data) => onSuccess(data),
    onError: (error: any) => {
      console.error("Error updating crew member:", error);
      alert(`Error updating crew member: ${error.message || "Unknown error"}`);
    },
  });

  const uploadImageMutation = useMutation({
    mutationFn: async () => {
      // TODO: Add profile image upload logic later
      console.warn("Profile image upload not implemented yet");
      return ""; // placeholder
    },
  });

  // Initialize form with crew member data if editing
  useEffect(() => {
    if (crewMember) {
      setFirstName(crewMember.first_name);
      setLastName(crewMember.last_name);
      setEmail(crewMember.email || "");
      setPhone(crewMember.phone || "");
      setRoleType(crewMember.role_type);
      setSpecialties(crewMember.specialties || []);
      setDayRate(crewMember.day_rate?.toString() || "");
      setHourlyRate(crewMember.hourly_rate?.toString() || "");
      setCurrency(crewMember.currency || "GBP");
      setTags(crewMember.tags || []);
      setNotes(crewMember.notes || "");
      setProfileImagePreview(crewMember.profile_image || null);

      // Address
      if (crewMember.address) {
        setStreet(crewMember.address.street || "");
        setCity(crewMember.address.city || "");
        setState(crewMember.address.state || "");
        setPostalCode(crewMember.address.postal_code || "");
        setCountry(crewMember.address.country || "");
      }

      // Payment details
      if (crewMember.payment_details) {
        setPaymentMethod(
          (crewMember.payment_details.payment_method as PaymentMethod) ||
            "Bank Transfer"
        );
        setAccountName(crewMember.payment_details.account_name || "");
        setAccountNumber(crewMember.payment_details.account_number || "");
        setSortCode(crewMember.payment_details.sort_code || "");
        setBankName(crewMember.payment_details.bank_name || "");
        setPaypalEmail(crewMember.payment_details.paypal_email || "");
        setOtherPaymentDetails(crewMember.payment_details.other_details || "");
      }

      // Emergency contact
      if (crewMember.emergency_contact) {
        setEmergencyName(crewMember.emergency_contact.name || "");
        setEmergencyRelationship(
          crewMember.emergency_contact.relationship || ""
        );
        setEmergencyPhone(crewMember.emergency_contact.phone || "");
      }

      // Availability
      if (crewMember.availability) {
        setGeneralAvailability(
          crewMember.availability.general_availability || ""
        );
      }
    }
  }, [crewMember]);

  // Handle profile image change
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfileImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Add specialty
  const handleAddSpecialty = () => {
    if (specialtyInput && !specialties.includes(specialtyInput)) {
      setSpecialties([...specialties, specialtyInput]);
      setSpecialtyInput("");
    }
  };

  // Remove specialty
  const handleRemoveSpecialty = (specialty: string) => {
    setSpecialties(specialties.filter((s) => s !== specialty));
  };

  // Add tag
  const handleAddTag = () => {
    if (tagInput && !tags.includes(tagInput)) {
      setTags([...tags, tagInput]);
      setTagInput("");
    }
  };

  // Remove tag
  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!firstName || !lastName || !roleType) {
      alert("Please fill in all required fields.");
      return;
    }

    // Validate that user is logged in
    if (!user) {
      alert("You must be logged in to perform this action.");
      return;
    }

    let profileImageUrl = crewMember?.profile_image || null;

    // Upload profile image if changed
    if (profileImage && user) {
      try {
        profileImageUrl = await uploadImageMutation.mutateAsync();
      } catch (error) {
        console.error("Error uploading profile image:", error);
      }
    }

    // Prepare crew member data
    const crewData = {
      first_name: firstName,
      last_name: lastName,
      email: email || null,
      phone: phone || null,
      profile_image: profileImageUrl,
      role_type: roleType,
      specialties,
      day_rate: dayRate ? parseFloat(dayRate) : null,
      hourly_rate: hourlyRate ? parseFloat(hourlyRate) : null,
      currency,
      payment_details: {
        payment_method: paymentMethod,
        ...(paymentMethod === "Bank Transfer" && {
          account_name: accountName || undefined,
          account_number: accountNumber || undefined,
          sort_code: sortCode || undefined,
          bank_name: bankName || undefined,
        }),
        ...(paymentMethod === "PayPal" && {
          paypal_email: paypalEmail || undefined,
        }),
        ...(otherPaymentDetails && { other_details: otherPaymentDetails }),
      },
      address: {
        street: street || undefined,
        city: city || undefined,
        state: state || undefined,
        postal_code: postalCode || undefined,
        country: country || undefined,
      },
      emergency_contact: {
        name: emergencyName || undefined,
        relationship: emergencyRelationship || undefined,
        phone: emergencyPhone || undefined,
      },
      availability: {
        general_availability: generalAvailability || undefined,
      },
      notes: notes || null,
      tags,
    };

    if (isEditing && crewMember) {
      // Update existing crew member
      updateCrewMutation.mutate({
        id: crewMember.id,
        updates: {
          ...crewData,
          updated_by: user.id,
        },
      });
    } else {
      // Add organization ID and user ID for new crew member
      const newCrewMember = {
        ...crewData,
        organization_id: orgId,
        created_by: user.id,
        updated_by: user.id,
      };

      // Create new crew member
      addCrewMutation.mutate(newCrewMember);
    }
  };

  // Define common input classes for consistency
  const inputClass =
    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const sectionClass = "mb-6 pb-6 border-b border-gray-200";
  const sectionTitleClass = "text-lg font-medium text-gray-900 mb-4";

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className={sectionClass}>
        <h2 className={sectionTitleClass}>Basic Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className={labelClass}>
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              id="firstName"
              type="text"
              className={inputClass}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="lastName" className={labelClass}>
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              id="lastName"
              type="text"
              className={inputClass}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="email" className={labelClass}>
              Email
            </label>
            <input
              id="email"
              type="email"
              className={inputClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="phone" className={labelClass}>
              Phone
            </label>
            <input
              id="phone"
              type="tel"
              className={inputClass}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="roleType" className={labelClass}>
              Role Type <span className="text-red-500">*</span>
            </label>
            <input
              id="roleType"
              type="text"
              className={inputClass}
              value={roleType}
              onChange={(e) => setRoleType(e.target.value)}
              placeholder="e.g. Technician, Engineer, Driver"
              required
            />
          </div>
          <div>
            <label htmlFor="profileImage" className={labelClass}>
              Profile Image
            </label>
            <div className="flex items-center space-x-4">
              {profileImagePreview && (
                <div className="h-16 w-16 rounded-full overflow-hidden">
                  <img
                    src={profileImagePreview}
                    alt="Profile preview"
                    className="h-full w-full object-cover"
                  />
                </div>
              )}
              <input
                id="profileImage"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="flex-1"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Specialties */}
      <div className={sectionClass}>
        <h2 className={sectionTitleClass}>Specialties</h2>
        <div className="space-y-4">
          <div className="flex space-x-2">
            <input
              type="text"
              className={inputClass}
              value={specialtyInput}
              onChange={(e) => setSpecialtyInput(e.target.value)}
              placeholder="Add a specialty"
            />
            <button
              type="button"
              onClick={handleAddSpecialty}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {specialties.map((specialty, index) => (
              <div
                key={index}
                className="bg-gray-100 rounded-full px-3 py-1 flex items-center"
              >
                <span className="text-sm">{specialty}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveSpecialty(specialty)}
                  className="ml-2 text-gray-500 hover:text-gray-700"
                >
                  &times;
                </button>
              </div>
            ))}
            {specialties.length === 0 && (
              <p className="text-sm text-gray-500">
                No specialties added yet. Add some to help categorize this crew
                member's skills.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Rates */}
      <div className={sectionClass}>
        <h2 className={sectionTitleClass}>Rates</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="dayRate" className={labelClass}>
              Day Rate
            </label>
            <input
              id="dayRate"
              type="number"
              step="0.01"
              min="0"
              className={inputClass}
              value={dayRate}
              onChange={(e) => setDayRate(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="hourlyRate" className={labelClass}>
              Hourly Rate
            </label>
            <input
              id="hourlyRate"
              type="number"
              step="0.01"
              min="0"
              className={inputClass}
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="currency" className={labelClass}>
              Currency
            </label>
            <select
              id="currency"
              className={inputClass}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              <option value="GBP">GBP (£)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="AUD">AUD (A$)</option>
              <option value="CAD">CAD (C$)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Address */}
      <div className={sectionClass}>
        <h2 className={sectionTitleClass}>Address</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="street" className={labelClass}>
              Street Address
            </label>
            <input
              id="street"
              type="text"
              className={inputClass}
              value={street}
              onChange={(e) => setStreet(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="city" className={labelClass}>
              City
            </label>
            <input
              id="city"
              type="text"
              className={inputClass}
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="state" className={labelClass}>
              State/Province
            </label>
            <input
              id="state"
              type="text"
              className={inputClass}
              value={state}
              onChange={(e) => setState(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="postalCode" className={labelClass}>
              Postal Code
            </label>
            <input
              id="postalCode"
              type="text"
              className={inputClass}
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="country" className={labelClass}>
              Country
            </label>
            <input
              id="country"
              type="text"
              className={inputClass}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Payment Details */}
      <div className={sectionClass}>
        <h2 className={sectionTitleClass}>Payment Details</h2>
        <div className="space-y-4">
          <div>
            <label htmlFor="paymentMethod" className={labelClass}>
              Payment Method
            </label>
            <select
              id="paymentMethod"
              className={inputClass}
              value={paymentMethod}
              onChange={(e) =>
                setPaymentMethod(e.target.value as PaymentMethod)
              }
            >
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="PayPal">PayPal</option>
              <option value="Cash">Cash</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {paymentMethod === "Bank Transfer" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="accountName" className={labelClass}>
                  Account Name
                </label>
                <input
                  id="accountName"
                  type="text"
                  className={inputClass}
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="accountNumber" className={labelClass}>
                  Account Number
                </label>
                <input
                  id="accountNumber"
                  type="text"
                  className={inputClass}
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="sortCode" className={labelClass}>
                  Sort Code
                </label>
                <input
                  id="sortCode"
                  type="text"
                  className={inputClass}
                  value={sortCode}
                  onChange={(e) => setSortCode(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="bankName" className={labelClass}>
                  Bank Name
                </label>
                <input
                  id="bankName"
                  type="text"
                  className={inputClass}
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                />
              </div>
            </div>
          )}

          {paymentMethod === "PayPal" && (
            <div>
              <label htmlFor="paypalEmail" className={labelClass}>
                PayPal Email
              </label>
              <input
                id="paypalEmail"
                type="email"
                className={inputClass}
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
              />
            </div>
          )}

          <div>
            <label htmlFor="otherPaymentDetails" className={labelClass}>
              Other Payment Details
            </label>
            <textarea
              id="otherPaymentDetails"
              rows={3}
              className={inputClass}
              value={otherPaymentDetails}
              onChange={(e) => setOtherPaymentDetails(e.target.value)}
              placeholder="Any additional payment information..."
            ></textarea>
          </div>
        </div>
      </div>

      {/* Emergency Contact */}
      <div className={sectionClass}>
        <h2 className={sectionTitleClass}>Emergency Contact</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="emergencyName" className={labelClass}>
              Name
            </label>
            <input
              id="emergencyName"
              type="text"
              className={inputClass}
              value={emergencyName}
              onChange={(e) => setEmergencyName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="emergencyRelationship" className={labelClass}>
              Relationship
            </label>
            <input
              id="emergencyRelationship"
              type="text"
              className={inputClass}
              value={emergencyRelationship}
              onChange={(e) => setEmergencyRelationship(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="emergencyPhone" className={labelClass}>
              Phone
            </label>
            <input
              id="emergencyPhone"
              type="tel"
              className={inputClass}
              value={emergencyPhone}
              onChange={(e) => setEmergencyPhone(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Availability */}
      <div className={sectionClass}>
        <h2 className={sectionTitleClass}>Availability</h2>
        <div>
          <label htmlFor="generalAvailability" className={labelClass}>
            General Availability
          </label>
          <textarea
            id="generalAvailability"
            rows={3}
            className={inputClass}
            value={generalAvailability}
            onChange={(e) => setGeneralAvailability(e.target.value)}
            placeholder="e.g. Available weekdays, not available on weekends, etc."
          ></textarea>
        </div>
      </div>

      {/* Notes */}
      <div className={sectionClass}>
        <h2 className={sectionTitleClass}>Notes</h2>
        <div>
          <textarea
            id="notes"
            rows={4}
            className={inputClass}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional notes about this crew member..."
          ></textarea>
        </div>
      </div>

      {/* Tags */}
      <div className={sectionClass}>
        <h2 className={sectionTitleClass}>Tags</h2>
        <div className="space-y-4">
          <div className="flex space-x-2">
            <input
              type="text"
              className={inputClass}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="Add a tag"
            />
            <button
              type="button"
              onClick={handleAddTag}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag, index) => (
              <div
                key={index}
                className="bg-primary/10 text-primary rounded-full px-3 py-1 flex items-center"
              >
                <span className="text-sm">{tag}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  className="ml-2 text-primary hover:text-primary/70"
                >
                  &times;
                </button>
              </div>
            ))}
            {tags.length === 0 && (
              <p className="text-sm text-gray-500">
                No tags added yet. Tags help you filter and find crew members
                quickly.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={() => navigate(`/org/${orgId}/crew`)}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          disabled={addCrewMutation.isPending || updateCrewMutation.isPending}
        >
          {addCrewMutation.isPending || updateCrewMutation.isPending
            ? "Saving..."
            : isEditing
            ? "Update Crew Member"
            : "Add Crew Member"}
        </button>
      </div>
    </form>
  );
}
